import React, { useState, useEffect } from 'react';
import {
  AppShell,
  Group,
  Title,
  ScrollArea,
  Button,
  Text
} from '@mantine/core';
import {
  IconLogout,
  IconMenu2,
  IconMenuDeep,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Module from './Module';
import DynamicMenu from './components/DynamicMenu';
import { useDynamicImport } from './hooks/useDynamicImport';


export default function Home({ user, onLogout }) {
  
  // États pour le menu dynamique
  const [dynamicOpenMenus, setDynamicOpenMenus] = useState({});
  const [dynamicOpenSubMenus, setDynamicOpenSubMenus] = useState({});
  const [currentMenuData, setCurrentMenuData] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const handleSelectModule = (path) => {
    navigate(path);
  };



  // Hook pour charger dynamiquement le composant selon la route
  const { component: DynamicComponent, loading: componentLoading } = useDynamicImport(
    currentMenuData?.component_path
  );

  // Fonction pour récupérer les données de menu par route
  useEffect(() => {
    const fetchMenuData = async () => {
      // Cas spécial pour Dashboard (route par défaut et /dashboard)
      if (!location.pathname || location.pathname === '/' || location.pathname === '/dashboard') {
        setCurrentMenuData({
          component_path: './Dashboard',
          module_title: 'Tableau de bord',
          module_subtitle: 'Vue d\'ensemble de l\'application'
        });
        return;
      }
      
      try {
        const response = await fetch(`/api/menu_items/by-route?path=${encodeURIComponent(location.pathname)}`);
        const data = await response.json();
        
        if (data.success) {
          setCurrentMenuData(data.data);
        } else {
          setCurrentMenuData(null);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données de menu:', error);
        setCurrentMenuData(null);
      }
    };

    fetchMenuData();
  }, [location.pathname]);

  const renderContent = () => {
    // Système entièrement dynamique basé sur menu_items ou données par défaut
    if (currentMenuData) {
      return (
        <Module 
          title={currentMenuData.module_title || ''} 
          subtitle={currentMenuData.module_subtitle || ''}
        >
          {componentLoading ? (
            <Text>Chargement du composant...</Text>
          ) : DynamicComponent ? (
            <DynamicComponent />
          ) : (
            <Text c="red">Erreur : Composant non trouvé</Text>
          )}
        </Module>
      );
    }

    // Fallback en cas d'erreur
    return (
      <Module title="Erreur" subtitle="Page non trouvée">
        <Text>La page demandée n'existe pas.</Text>
      </Module>
    );
  };

  return (
    <AppShell
      navbar={{
        width: menuCollapsed ? 60 : 250,
        breakpoint: 'sm'
      }}
      header={{ height: 60 }}
      footer={{ height: 50 }}
      padding="md"
      styles={{
        navbar: {
          transition: 'width 0.3s ease'
        }
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setMenuCollapsed(!menuCollapsed)}
              title={menuCollapsed ? "Agrandir le menu" : "Réduire le menu"}
            >
              {menuCollapsed ? <IconMenuDeep size={18} /> : <IconMenu2 size={18} />}
            </Button>
            <Title order={3}>React TMD</Title>
          </Group>
          <Group>
            <Text size="sm">Bienvenue, {user.prenom && user.nom ? `${user.prenom} ${user.nom}` : user.email} !</Text>
            <Button
              variant="light"
              leftSection={<IconLogout size={16} />}
              onClick={onLogout}
            >
              Déconnexion
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p={menuCollapsed ? "xs" : "md"}>
        {menuCollapsed ? null : (
          <ScrollArea
            styles={{
              root: {
                '& .mantine-NavLink-root': {
                  borderRadius: '4px',
                  marginBottom: '2px',
                },
                '& .mantine-NavLink-root[data-active]': {
                  backgroundColor: 'var(--mantine-color-blue-light)',
                  color: 'var(--mantine-color-blue-7)',
                  fontWeight: 500,
                },
                '& .mantine-NavLink-root:hover': {
                  backgroundColor: 'var(--mantine-color-gray-1)',
                }
              }
            }}
          >
            <DynamicMenu 
              onSelectModule={handleSelectModule}
              currentPath={location.pathname}
              openMenus={dynamicOpenMenus}
              setOpenMenus={setDynamicOpenMenus}
              openSubMenus={dynamicOpenSubMenus}
              setOpenSubMenus={setDynamicOpenSubMenus}
            />
          </ScrollArea>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        {renderContent()}
      </AppShell.Main>

      <AppShell.Footer>
        <Group h="100%" px="md" justify="center" align="center">
          <Text size="sm" c="dimmed">
            © 2024 React TMD - Tous droits réservés • Version 1.0.0 - Développé avec React & Mantine
          </Text>
        </Group>
      </AppShell.Footer>
    </AppShell>
  );
} 